import React, { useState } from "react";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "../context/ContentContext";
import { saveContent, formatApiError, triggerRebuild } from "../lib/api";
import { setByPath, deepClone } from "../lib/paths";
import { CONTENT_SCHEMA } from "./contentSchema";
import { Field } from "./AdminField";

export const ContentEditor = () => {
  const { content, setContent } = useContent();
  const [draft, setDraft] = useState(() => deepClone(content));
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState("hero");

  const update = (path, value) => setDraft((d) => setByPath(d, path, value));

  const save = async () => {
    setSaving(true);
    try {
      const saved = await saveContent(draft);
      setContent(saved);
      triggerRebuild("content edited");
      toast.success("Content saved — changes are live. Site rebuild starting in the background.");
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6 sticky top-0 bg-cream py-3 z-10">
        <div>
          <h1 className="font-serif text-3xl text-ink">Website content</h1>
          <p className="text-sm text-ink-muted">Edit any text or image, then save. Changes go live instantly.</p>
        </div>
        <button data-testid="save-content-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-ink text-cream px-6 py-3 text-sm font-semibold hover:bg-sage-dark transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </button>
      </div>

      <div className="space-y-3">
        {CONTENT_SCHEMA.map((section) => {
          const isOpen = openSection === section.key;
          return (
            <div key={section.key} className="rounded-xl border border-cream-2 bg-white overflow-hidden">
              <button
                data-testid={`section-toggle-${section.key}`}
                onClick={() => setOpenSection(isOpen ? "" : section.key)}
                className="w-full flex items-center justify-between px-5 py-4 text-left"
              >
                <span className="font-serif text-xl text-ink">{section.label}</span>
                <span className="text-ink-muted text-sm">{isOpen ? "Hide" : "Edit"}</span>
              </button>
              {isOpen && (
                <div className="px-5 pb-6 pt-1 space-y-5 border-t border-cream-2">
                  {section.fields.map((field) => (
                    <Field key={field.path} field={field} draft={draft} update={update} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContentEditor;
